import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import moment from 'moment';

import {
  BoxExpand,
  Button,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  Table,
  Tag,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { AbsenceApprovalStatusesEnum } from '@thinknet-gmbh/platform-common';
import { AbsenceTypesEnum } from '@thinknet-gmbh/platform-common';

import { useGetCurrentEmployeeAbsences } from 'src/hooks/employee-absences/get-current-absences.hook';
import { APPROVAL_STATUS_STYLES } from 'src/pages/employee/sub-pages/absence-management/components/absence-list-table/components/approval-status-render.constants';
import { CreateAbsence } from 'src/pages/employee/sub-pages/employee-list/components/create-absence';

import { UserAbsenceWrapper, TagWrapper } from './user-absence.styles';

const UserAbsence = () => {
  const { t } = useTranslation('employee');
  const [isOpenedCreateAbsenceModal, setIsOpenedCreateAbsenceModal] = useState(false);

  const { memberAbsences, loading, paginationConfig } = useGetCurrentEmployeeAbsences({
    isAbsencesToApprove: false,
  });

  return (
    <UserAbsenceWrapper>
      <CreateAbsence
        isOpened={isOpenedCreateAbsenceModal}
        onClose={() => setIsOpenedCreateAbsenceModal(false)}
      />
      <BoxExpand
        title={t('labels.myAbsence')}
        separator
        titleFontSize={TYPOGRAPHY_SIZE.medium}
        buttons={
          <Button
            type="primary"
            ghost
            icon={ICONS.plus}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setIsOpenedCreateAbsenceModal(true)}
          >
            {t('labels.createAbsence')}
          </Button>
        }
      >
        <Table
          dataSource={memberAbsences}
          rowKey={record => record._id}
          loading={loading}
          pagination={paginationConfig}
          t={t}
        >
          <Table.Column
            title={t('labels.absenceType')}
            key="type"
            dataIndex={['absenceReason', 'name']}
            render={text => <Typography>{text}</Typography>}
          />
          <Table.Column
            title={`${t('labels.paid')}/${t('labels.unpaid')}`}
            key="paymentType"
            dataIndex="paymentType"
            render={paymentType => (
              <TagWrapper>
                <Icon
                  icon={
                    paymentType === AbsenceTypesEnum.PAID
                      ? ICONS.checkCircleRegular
                      : ICONS.xCircleRegular
                  }
                  color={
                    paymentType === AbsenceTypesEnum.PAID
                      ? PLATFORM_THEME_COLORS.tone.green
                      : PLATFORM_THEME_COLORS.tone.red
                  }
                  size={22}
                  rightMargin={false}
                />
                <Typography
                  fontColor={
                    paymentType === AbsenceTypesEnum.PAID
                      ? PLATFORM_THEME_COLORS.tone.green
                      : PLATFORM_THEME_COLORS.tone.red
                  }
                >
                  {t(`labels.${paymentType.toLowerCase()}`)}
                </Typography>
              </TagWrapper>
            )}
          />
          <Table.Column
            title={t('labels.date')}
            key="date"
            render={absence => {
              const from = moment(absence.from).format('DD.MM.YYYY');
              const to = moment(absence.to).format('DD.MM.YYYY');

              return <Typography>{`${from} - ${to}`}</Typography>;
            }}
          />
          <Table.Column
            title={t('labels.hours')}
            key="hours"
            render={absence => {
              if (!absence.allDay) {
                const from = moment(absence.from).format('HH:mm');
                const to = moment(absence.to).format('HH:mm');

                return <Typography>{`${from} - ${to}`}</Typography>;
              }

              return <Typography>-</Typography>;
            }}
          />
          <Table.Column
            title={t('labels.approvalStatus')}
            key="approvalStatus"
            dataIndex="approvalStatus"
            render={approvalStatus => {
              const { background, fontColor } =
                APPROVAL_STATUS_STYLES[approvalStatus as AbsenceApprovalStatusesEnum];

              return (
                <Tag background={background} fontColor={fontColor}>
                  {t(`labels.${approvalStatus.toLowerCase()}`)}
                </Tag>
              );
            }}
          />
          <Table.Column
            title={t('labels.reason')}
            key="details"
            dataIndex="details"
            render={details => <Typography>{details ? details : '-'}</Typography>}
          />
          <Table.Column
            title={t('labels.nameOfApprover')}
            key="approver"
            dataIndex="approver"
            render={approver => <Typography>{approver.profile.displayName}</Typography>}
          />
        </Table>
      </BoxExpand>
    </UserAbsenceWrapper>
  );
};

export default UserAbsence;
